/**
 * What a toast is, and how long it gets to be read.
 *
 * A `.ts` module rather than part of the provider so the rule below is
 * testable without rendering anything — the whole of it is arithmetic over a
 * kind, and the kind is what decides an accessibility outcome.
 */

export type ToastKind = 'success' | 'info' | 'error'

export type ToastInput = {
  kind: ToastKind
  title: string
  description?: string
}

export type Toast = ToastInput & { id: number }

/** How long a confirmation stays up. Long enough to read, short enough to go. */
export const TOAST_DISMISS_MS = 3_500

/**
 * How long an ERROR stays up — which is: until somebody closes it.
 *
 * Every toast used to vanish after 3.5 seconds, errors included, and that is a
 * message the reader is not guaranteed to get. A confirmation is a courtesy —
 * missing "Zapisano" costs nothing, because the thing was saved either way. An
 * error is the opposite: it is the only account of what went wrong and what to
 * do instead, it arrives while the reader is still looking at the field they
 * were typing in, and it is exactly the case WCAG 2.2.1 has in mind when it
 * asks that timed content be pausable or extendable.
 *
 * `null` = no timer at all, which is the simplest way to satisfy that: nothing
 * to pause because nothing expires. The close button and the next navigation
 * both get rid of it.
 */
export function dismissAfterMs(kind: ToastKind): number | null {
  return kind === 'error' ? null : TOAST_DISMISS_MS
}

/**
 * The ARIA role for a message of this kind.
 *
 * `alert` is assertive: the screen reader interrupts whatever it was saying.
 * That is rude, and being rude is correct exactly once — when the thing the
 * person just tried to do did not happen. Everything else is `status`, which
 * waits its turn.
 */
export function toastRole(kind: ToastKind): 'alert' | 'status' {
  return kind === 'error' ? 'alert' : 'status'
}

/** Border + tint per kind. A lookup rather than the nested ternary it replaces. */
export const TOAST_ACCENT: Record<ToastKind, string> = {
  success: 'border-emerald-400/40 bg-emerald-400/10',
  error: 'border-rose-400/40 bg-rose-400/10',
  info: 'border-white/15 bg-white/[0.06]',
}

/** The dot beside the title, same idea. */
export const TOAST_DOT: Record<ToastKind, string> = {
  success: 'bg-emerald-400',
  error: 'bg-rose-400',
  info: 'bg-white/60',
}
